<template>
  <LoadingSpinner v-if="isLoading" />

  <div v-else class="show-invoice no-print">
    <AppPageHeader :title="`Invoice# ${invoice.invoiceId }`">
      <template #actions>
        <div class="flex gap-x-2">
          <BaseButton
            v-if="invoice.status !== 'paid'"
            text="Record Payment"
            @click="showPaymentModal = true"
          />
          <BaseDropdown
            title="Manage"
            right
            :items="[
              { title: 'Edit Invoice', to: { name: 'EditInvoice', params: { id: invoice.id } } },
              { title: 'Email to client', action: generateInvoicePdf },
              { title: 'Print', action: print },
              { title: 'Delete', action: () => showDeleteConfirmationModal = true }
            ]"
          />
        </div>
      </template>
    </AppPageHeader>

    <!-- Invoice details -->
    <div id="hiddenContent" class="grid grid-cols-6 gap-x-2 border-b border-slate-300 pb-4 mb-4">
      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Status</h3>
        <BaseBadge
          :variant="invoice.status === 'paid' ? 'success' : invoice.status === 'outstanding' ? 'error' : 'warning'"
        >
          <span class="capitalize">{{ invoice.status }}</span>
        </BaseBadge>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Due Date</h3>
        <span class="text-sm font-weight-bold">{{ new Date(invoice.dueDate).toLocaleDateString() }}</span>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Invoice #</h3>
        <span class="text-sm font-weight-bold">{{ invoice.invoiceId }}</span>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Project</h3>
        <span class="text-sm font-weight-bold">
          <router-link v-if="project?.id" :to="{ name: 'ShowProject', params: { projectId: project?.id, customerId: project?.customerId } }">
            {{ project?.name }}
          </router-link>
          <span v-else>{{ invoice.projectId }}</span>
        </span>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Invoice Date</h3>
        <span class="text-sm font-weight-bold">{{ new Date(invoice.invoiceDate).toLocaleDateString() }}</span>
      </div>

      <div>
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Balance</h3>
        <span class="text-sm font-weight-bold">{{ currency(invoice.balance) }}</span>
      </div>
    </div>

    <!-- Invoice items -->
    <BaseTable
      :items="invoice.items"
      :fields="invoiceItemFields"
      class="mt-5"
    >
      <template #title-data="{ row }">
        {{ row.title }}
      </template>
      <template #total-data="{ row }">
        <span class="d-block text-right">{{ currency(row.total) }}</span>
      </template>
    </BaseTable>

    <hr class="border-b-slate-200 border-2" />

    <!-- totals -->
    <div class="mt-5 flex flex-col bg-slate-100 w-1/3 p-4 rounded-lg sm:ml-auto">
      <div class="flex justify-between mb-4">
        <h3 class="text-sm font-medium leading-6 text-slate-500">Credit</h3>
        <span v-if="invoice.credit" class="text-sm font-weight-bold">{{ currency(invoice.credit) }}</span>
        <span v-else>$0.00</span>
      </div>

      <div class="flex justify-between mb-4">
        <h3 class="text-sm font-medium leading-6 text-slate-500">Total</h3>
        <span v-if="invoice.total" class="text-sm font-weight-bold">{{ currency(invoice.total) }}</span>
        <span v-else>$0.00</span>
      </div>

      <div class="flex justify-between">
        <h3 class="text-sm font-medium leading-6 text-slate-500">Balance</h3>
        <span class="text-sm font-weight-bold">
          <span v-if="invoice.balance">{{ currency(invoice.balance) }}</span>
          <span v-else>$0.00</span>
        </span>
      </div>
    </div>

    <!-- Payment activities -->
    <div class="mt-8">
      <h4>Payments Activities</h4>
      <BaseTable
        v-if="payments && payments.length > 0"
        :items="payments"
        :fields="paymentsFields"
      >
        <template #date-data="{ row }">
          {{ formatDate(new Date(row.date)) }}
        </template>
        <template #method-data="{ row }">
          {{ row.method }}
        </template>
        <template #memo-data="{ row }">
          <span v-html="row.memo" />
        </template>
        <template #amount-data="{ row }">
          {{ currency(row.amount) }}
        </template>
        <template #action-data="{ row }">
          <BaseButton
            :disabled="isDeletingPayment"
            :class="{ 'cursor-not-allowed opacity-50': isDeletingPayment }"
            @click="deletePayment(row.id)"
          >
            <div class="flex justify-between items-center gap-2">
              <span>Delete</span>
              <span
                v-if="isDeletingPayment && deletingPaymentId === row.id"
                class="i-heroicons-arrow-path animate-spin"
              />
            </div>
          </BaseButton>
        </template>
      </BaseTable>
    </div>

    <DeleteConfirmationModal
      id="deleteConfirmationModal"
      v-model="showDeleteConfirmationModal"
      title="Delete Invoice"
      message="Are you sure you want to delete this invoice?"
      :is-deleting="isPending"
      @delete="deleteInvoice"
    />


    <Modal
      id="email-purchase-order"
      v-model="showEmailModal"
      title="Email Invoice"
      :no-escape="true"
      :no-backdrop="true"
      :no-close-button="true"
      width="w-full md:w-[80%] lg:max-w-5xl xl:max-w-6xl"
    >
      <EmailInvoice
        :invoice="invoice"
        :pdf-ready="pdfReady"
        :pdf-link="pdfLink"
        :email="(email as Email)"
        @close="showEmailModal = false"
      />
    </Modal>

    <Modal
      v-model="showPaymentModal"
      title="Record Payment"
      width="w-full md:w-1/2"
      :no-escape="true"
      :no-backdrop="true"
      :no-close-button="true"
    >
      <template #body>
        <InvoicePaymentForm
          v-model="invoicePayment"
          :invoice="invoice"
          :is-pending="isMakingPayment"
          @save="makePayment"
          @cancel="showPaymentModal = false"
        />
      </template>
    </Modal>

  </div>

  <div
    v-if="!isLoading"
    id="printContent"
    class="hide-print-content"
  >
    <PrintInvoice
      :invoice="invoice"
      :customer="customer"
    />
  </div>

  <div
    v-if="!isLoading && invoice.id"
    class="no-print"
  >
    <div
      v-show="!pdfReady"
      ref="invoiceToPdf"
    >
      <InvoiceToPdf
        :invoice="invoice"
        :customer="customer"
      />
    </div>
  </div>


</template>

<script lang="ts" setup>
import AppPageHeader from '@/components/app/AppPageHeader.vue'
import BaseDropdown from '@/components/base/BaseDropdown.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import EmailInvoice from '../component/EmailInvoice.vue'
import PrintInvoice from '../component/PrintInvoice.vue'
import InvoiceToPdf from '../component/InvoiceToPdf.vue'
import html2pdf from 'html2pdf.js'
import DeleteConfirmationModal from '@/components/app/DeleteConfirmationModal.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { useHead } from '@unhead/vue'
import { useRoute, useRouter } from 'vue-router'
import { useMutation, useQuery, useQueryClient} from '@tanstack/vue-query'
import { useNotification } from '@kyvg/vue3-notification'
import InvoiceService from '@/services/InvoiceService'
import ProjectService from '@/services/ProjectService'
import { watch, ref, computed, useTemplateRef } from 'vue'
import { currency, formatDate, toNumber } from '@/utils'
import CustomerService from '@/services/CustomerService'
import Modal from '@/components/app/AppModal.vue'
import { Email } from '@/models/email'
import EmailService, { EmailTemplate } from '@/services/EmailService'
import Handlebars from 'handlebars'
import InvoicePaymentForm from '../component/InvoicePaymentForm.vue'
import { INVOICE_STATUS } from '@/models/invoice'
import { extend } from 'underscore'

useHead({
  title: 'Invoice'
})

const { notify } = useNotification()
const $router = useRouter()
const $route = useRoute()
const invoiceId = $route.params.id as string
const invoiceItemFields = [
  { key: 'title', label: 'Items' },
  { key: 'total', label: 'Total' }
]
const paymentsFields = [
  { key: 'date', label: 'Date' },
  { key: 'method', label: 'Method' },
  { key: 'memo', label: 'Memo' },
  { key: 'amount', label: 'Amount' },
  { key: 'action', label: '' }
]

const showPaymentModal = ref(false)
const pdfReady = ref(true)
const pdfLink = ref('')
const showEmailModal = ref(false)
const showDeleteConfirmationModal = ref(false)
const invoiceService = new InvoiceService()
const queryClient = useQueryClient()

const { data: invoice, isLoading: invoiceLoading, isError: invoiceError } = useQuery({
  queryKey: ['invoice', invoiceId],
  queryFn: async () => invoiceService.find(invoiceId)
})

const enablePaymentsQuery = computed(() => !!invoice.value?.id)
const { data: payments } = useQuery({
  queryKey: ['invoicePayments', invoiceId],
  queryFn: async () => invoiceService.getPayments(invoiceId),
  enabled: enablePaymentsQuery
})

const enableProjectQuery = computed(() => !!invoice.value?.projectId)
const { data: project, isLoading: projectLoading, isError: projectError } = useQuery({
  queryKey: ['projects', invoice.value?.projectId],
  queryFn: () => new ProjectService().find(invoice.value?.projectId),
  enabled: enableProjectQuery
})

const enableCustomerQuery = computed(() => !!invoice.value?.customerId)
const { data: customer, isLoading: customerLoading, isError: customerError } = useQuery({
  queryKey: ['customers', invoice.value?.customerId],
  queryFn: () => new CustomerService().find(invoice.value?.customerId),
  enabled: enableCustomerQuery
})

watch([invoiceError, projectError, customerError], (value) => {
  if (value) {
    notify({
      type: 'error',
      text: 'Failed to fetch data',
    })
  }
})


const isLoading = computed(() => {
  return invoiceLoading.value || projectLoading.value || customerLoading.value
})

const print = () => {
  window.print()
}

const invoicePayment = ref({
  date: new Date().toISOString().split('T')[0],
  method: '',
  amount: 0,
  memo: ''
})

const emailInvoice = () => {
  const link = `${window.location.origin}/invoice/${invoice.value.id}`
  const emailData = { customerName: customer.value.name, link, invoiceId: invoice.value.invoiceId }

  email.value.to = [customer.value.email]
  email.value.from = 'portal-admin@uttrly.com'

  email.value.message.subject = Handlebars.compile(emailTemplate.value.subject).call(this, { invoiceId: invoice.value.invoiceId })

  Handlebars.registerPartial(baseTempalte.value.name, baseTempalte.value.html)
  email.value.message.html = Handlebars.compile(emailTemplate.value.html).call(this, emailData)
  showEmailModal.value = true
}

const email = ref({...new Email(), to: [], cc: []})
const emailService = new EmailService()

const { data: baseTempalte } = useQuery({
  queryKey: ['emailTemplates/base_layout'],
  queryFn: async () => {
    return await emailService.find('base_layout') || new EmailTemplate()
  }
})

const { data: emailTemplate } = useQuery({
  queryKey: ['emailTemplates/send_invoice'],
  queryFn: async () => {
    return await emailService.find('send_invoice') || new EmailTemplate()
  }
})

const invoiceToPdf = useTemplateRef('invoiceToPdf')

const generateInvoicePdf = async () => {
  emailInvoice()
  showEmailModal.value = true
  pdfReady.value = false

  const opt = {
    margin: 0.5,
    filename: 'Invoice.pdf',
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
  }

  const attachment = await html2pdf().from(invoiceToPdf.value).set(opt).toPdf().get('pdf').then((pdf) => {
    const blob = pdf.output('blob')
    pdfLink.value = URL.createObjectURL(blob)
    pdfReady.value = true
    return blob
  })

  const reader = new FileReader()
  reader.readAsDataURL(attachment)
  reader.onloadend = () => {
    const base64data = (reader.result as string).split(',')[1]
    email.value.message.attachments = [{
      filename: 'Invoice.pdf',
      content: base64data,
      encoding: 'base64'
    }]
    pdfReady.value = true
  }
}


const { mutateAsync: deleteInvoice, isPending } = useMutation({
  mutationFn: () => invoiceService.deleteInvoice(invoice.value.id),
  onSuccess: () => {
    queryClient.removeQueries({ queryKey: ['invoice', invoiceId] })
    queryClient.invalidateQueries({ queryKey: ['firestore','invoices'], exact: false })
    queryClient.invalidateQueries({ queryKey: ['invoicedValue', invoice.value?.projectId] })
    queryClient.invalidateQueries({ queryKey: [ 'invoices', invoice.value?.projectId ] })
    notify({
      type: 'success',
      title: 'Invoice Deleted',
      text: 'Invoice deleted successfully'
    })
    $router.push({ name: 'ListInvoices' })
  },
  onError: (error) => {
    showDeleteConfirmationModal.value = false
    notify({
      type: 'error',
      title: 'Error deleting invoice',
      text: error.message || 'An error occurred while deleting the invoice'
    })
  }
})

const deletingPaymentId = ref('')
const { mutateAsync: deletePayment, isPending: isDeletingPayment } = useMutation({
  mutationFn: async (paymentId: string) => {
    deletingPaymentId.value = paymentId
    // delete payment
    await invoiceService.deletePayment(invoice.value.id, paymentId)

    // update invoice status and balance
    const updatedInvoice = {
      ...extend({}, invoice.value),
      balance: toNumber(invoice.value.balance) + toNumber((payments?.value.find(p => p.id === paymentId).amount) || 0),
      status: INVOICE_STATUS.OUTSTANDING
    }

    await invoiceService.update(invoice.value.id, updatedInvoice)

  },
  onSuccess: () => {
    // Refresh the invoice data
    queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
    queryClient.invalidateQueries({ queryKey: ['invoicePayments', invoiceId] })
    queryClient.invalidateQueries({ queryKey: ['firestore', 'invoices'], exact: false })
    deletingPaymentId.value = ''
    notify({
      type: 'success',
      title: 'Payment Deleted',
      text: 'Payment has been successfully deleted'
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error Deleting Payment',
      text: error.message || 'An error occurred while deleting the payment'
    })
  }
})

const { mutateAsync: makePayment, isPending: isMakingPayment } = useMutation({
  mutationFn: async (payment: typeof invoicePayment.value) => {
    try {
      // Save the payment
      await invoiceService.addPayment(invoice.value.id, payment)

      // Calculate new balance
      const newBalance = toNumber(invoice.value.balance) - toNumber(payment.amount)

      // Update invoice status and balance
      const updatedInvoice = {
        ...invoice.value,
        balance: newBalance,
        status: newBalance <= 0 ? INVOICE_STATUS.PAID : invoice.value.status
      }

      await invoiceService.update(invoice.value.id, updatedInvoice)

      // Refresh the invoice data
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoicePayments', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['firestore', 'invoices'], exact: false })

      // Close the payment form
      showPaymentModal.value = false

      // Reset payment form
      invoicePayment.value = {
        date: new Date().toISOString().split('T')[0],
        method: '',
        amount: 0,
        memo: ''
      }

      // Show success notification
      notify({
        type: 'success',
        title: 'Payment Recorded',
        text: 'Payment has been successfully recorded'
      })
    } catch (error) {
      console.error(error)
      notify({
        type: 'error',
        title: 'Error Recording Payment',
        text: error.message || 'An error occurred while recording the payment'
      })
    }
  }
})

</script>

<style lang="scss" scoped>
@media print {
  .no-print {
    display: none;
  }
  #printContent {
    display: block;
  }
}

.hide-print-content {
  display: none;
}
</style>

